import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    SchemaValidator,
    ValidationResult,
} from '../validation/schemaValidator';

interface UseSchemaValidationOptions {
    debounceMs?: number;
    enabled?: boolean;
}

export default function useSchemaValidation(
    activationId: number,
    options: UseSchemaValidationOptions = {},
) {
    const { debounceMs = 500, enabled = true } = options;

    const [validationResult, setValidationResult] =
        useState<ValidationResult | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [lastValidatedDefinition, setLastValidatedDefinition] = useState<
        string | null
    >(null);

    // Get the current state from Redux
    const experienceState = useSelector(
        (state: any) => state.experiences?.present,
    );
    const screensState = useSelector((state: any) => state.screens?.present);
    const flowsState = useSelector((state: any) => state.flows?.present);

    // Create the current definition from Redux state
    const currentDefinition = useMemo(() => {
        if (!experienceState || !screensState) {
            return null;
        }

        const localExperience = experienceState.entities[activationId];
        if (!localExperience) {
            return null;
        }

        const localScreens = screensState.entities;
        const screenIds = localExperience.screens || [];

        const screens: any[] = [];
        screenIds.forEach((screenId: string) => {
            const screen = localScreens[screenId];
            if (screen) {
                screens.push(screen);
            }
        });

        // Handle processing flows
        const flows: Record<string, any> = {};
        if (flowsState && flowsState.legacyFlows) {
            Object.keys(flowsState.legacyFlows).forEach((flowId: string) => {
                flows[flowId] = flowsState.legacyFlows[flowId];
            });
        }

        // Handle sharing stations
        const sharing: any[] = [];
        if (localExperience.sharing) {
            localExperience.sharing.forEach((sharingStation: any) => {
                const sharingScreens: any[] = [];
                if (sharingStation.screens) {
                    sharingStation.screens.forEach((screenId: string) => {
                        const screen = localScreens[screenId];
                        if (screen) {
                            sharingScreens.push(screen);
                        }
                    });
                }

                sharing.push({
                    name: sharingStation.name,
                    screens: sharingScreens,
                });
            });
        }

        return {
            version: 1,
            definitionVersion: 1,
            screens,
            processingFlows: flows,
            exitPin: localExperience.exitPin,
            sharing: sharing.length > 0 ? sharing : undefined,
            activationId,
        };
    }, [activationId, experienceState, screensState, flowsState]);

    // Debounced validation function
    const validateDefinition = useCallback(
        async (definition: any) => {
            if (!enabled || !definition) {
                setValidationResult(null);
                return;
            }

            const definitionString = JSON.stringify(definition);

            // Skip validation if definition hasn't changed
            if (definitionString === lastValidatedDefinition) {
                return;
            }

            setIsValidating(true);

            try {
                // Add a small delay to simulate async validation
                // This allows the UI to show the loading state
                await new Promise((resolve) => setTimeout(resolve, 50));

                const validator = SchemaValidator.getInstance();
                const result = validator.validateDefinition(definition);

                setValidationResult(result);
                setLastValidatedDefinition(definitionString);
            } catch (error) {
                console.error('Schema validation error:', error);
                setValidationResult({
                    isValid: false,
                    errors: [
                        {
                            path: 'validation',
                            message:
                                'Failed to validate schema: ' +
                                (error as Error).message,
                            severity: 'error',
                        },
                    ],
                    warnings: [],
                });
            } finally {
                setIsValidating(false);
            }
        },
        [enabled, lastValidatedDefinition],
    );

    // Debounced effect for validation
    useEffect(() => {
        if (!currentDefinition) {
            setValidationResult(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            validateDefinition(currentDefinition);
        }, debounceMs);

        return () => clearTimeout(timeoutId);
    }, [currentDefinition, validateDefinition, debounceMs]);

    // Manual validation trigger (for immediate validation)
    const validateNow = useCallback(() => {
        if (currentDefinition) {
            setLastValidatedDefinition(null); // Force re-validation
            validateDefinition(currentDefinition);
        }
    }, [currentDefinition, validateDefinition]);

    return {
        validationResult,
        isValidating,
        validateNow,
        currentDefinition,
        hasErrors: validationResult
            ? validationResult.errors.length > 0
            : false,
        hasWarnings: validationResult
            ? validationResult.warnings.length > 0
            : false,
        isValid: validationResult ? validationResult.isValid : true,
        errorCount: validationResult ? validationResult.errors.length : 0,
        warningCount: validationResult ? validationResult.warnings.length : 0,
    };
}
